import { useEffect, useRef, useState, useCallback } from 'react';
import widgetManager from './widget-manager';
import type { BlazeEventHandlers } from './types';
import Loader from '../../components/loader';

/**
 * Blaze Widget React Component
 * Receives { data, apiUrl } props like other widgets
 */
const BlazeWidget = ({
  data, 
  viewerCountry,
  requireAuth,
  eventHandlers,
  labels,
  searchText,
  orderBy
}: { 
  data: any; 
  viewerCountry?: string;
  requireAuth?: boolean;
  eventHandlers?: BlazeEventHandlers;
  labels?: string;
  searchText?: string;
  orderBy?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const widgetRef = useRef<any>(null);
  const isInitialMount = useRef<boolean>(true);
  
  const uniqueContainerId = useRef<string>(
    `blaze-widget-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15)}`
  );
  
  const buildConfig = useCallback((widgetData: any, labelsProp?: string) => {
    const isGeoBased = Boolean(widgetData?.settings?.isGeoBased || widgetData?.isGeoBased);
    
    let effectiveViewerCountry: string | undefined = viewerCountry || widgetData?.viewerCountry;
    if (isGeoBased && !effectiveViewerCountry && typeof navigator !== 'undefined') {
      const lang = navigator?.language || (Array.isArray((navigator as any).languages) ? (navigator as any).languages[0] : '');
      const region = lang && lang.includes('-') ? lang.split('-')[1] : '';
      if (region && region.length === 2) {
        effectiveViewerCountry = region.toUpperCase();
      }
    }
    
    return {
      ...widgetData,
      isGeoBased,
      viewerCountry: effectiveViewerCountry,
      containerId: uniqueContainerId.current,
      requireAuth,
      labels: labelsProp,
      searchText,
      orderBy,
      eventHandlers
    };
  }, [viewerCountry, requireAuth, searchText, orderBy, eventHandlers]);

  const initializeWidget = useCallback(async (widgetData: any, labelsProp?: string) => {
    try {
      setError(null);
      setLoading(true);
      if (!containerRef?.current) {
        throw new Error('Container ref not available');
      }
      
      const width = widgetData?.settings?.width || '100%';
      const height = widgetData?.settings?.height || '300px';
      containerRef.current.style.setProperty('width', width);
      containerRef.current.style.setProperty('height', height);
      containerRef.current.style.setProperty('display', 'block');
      containerRef.current.parentElement?.style.setProperty('overflow', 'auto');

      const config = buildConfig(widgetData, labelsProp);
      const widgetInstance = await widgetManager.createWidget(config);
      widgetRef.current = widgetInstance;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      eventHandlers?.onError?.(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [buildConfig, eventHandlers]);

  const updateWidget = useCallback(async (widgetData: any, labelsProp?: string) => {
    if (!widgetRef.current) {
      console.warn('Cannot update widget: widget not initialized');
      setLoading(false);
      return;
    }

    try {
      widgetManager.destroyWidget(uniqueContainerId.current);
      widgetRef.current = null;
      await initializeWidget(widgetData, labelsProp);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      eventHandlers?.onError?.(errorMessage);
      setError(errorMessage);
    }
  }, [initializeWidget, eventHandlers]);

  useEffect(() => {
    if (!data) return;
    
    if (isInitialMount.current) {
      isInitialMount.current = false;
      initializeWidget(data, labels);
    } 
    else {
      setLoading(true);
      updateWidget(data, labels);
    }
  }, [data, labels, searchText, orderBy, initializeWidget, updateWidget]);

  useEffect(() => {
    const containerId = uniqueContainerId.current;
    return () => {
      if (widgetRef?.current) {
        widgetManager.destroyWidget(containerId);
        widgetRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <></>

  return (
    <div ref={containerRef}>
      {loading && <Loader />}
      <div id={uniqueContainerId.current} style={{ width: '100%', height: '100%'}} />
    </div>
  );
};

export default BlazeWidget; 